import React from "react";
import { Box, Typography } from "@mui/material";
import "leaflet/dist/leaflet.css";
import "./leafletDefaultIcon";
import { useMapState } from "./hooks/useMapState";
import { useMapData } from "./hooks/useMapData";
import { MapViewProps } from "./types";
import MapRenderer from "./components/MapRenderer";
import MapAnimations from "./components/MapAnimations";

/**
 * Modulare MapView-Komponente
 * Koordiniert alle Map-Funktionalitäten über Hooks und Komponenten
 */
const MapView: React.FC<MapViewProps> = ({
  projectName,
  coordinates,
  height = "240px",
  zoom = 12,
  geometrie,
  anlagen = [],
  showLabel = true,
  showBorder = true,
  noPadding = false,
  parcels,
  onParcelUpdate,
  onParcelClick,
  selectedParcelId,
  selectedAnlageId,
  showLayerControl = true,
  showMenuButton = true,
  onShapefileUploadClick,
  onAlkisUploadClick,
  onAnlagePositionChange,
  onAnlageSelect,
  onMapClick,
  standortLocation,
}) => {
  const [isMapHovered, setIsMapHovered] = React.useState(false);

  const { layerVisibility, toggleLayer } = useMapState();

  const { flaecheCoordinates, anlagenCoordinates, mapCenter } = useMapData({
    projectName,
    coordinates,
    geometrie,
    anlagen,
  });

  return (
    <Box
      sx={{
        mb: noPadding ? 0 : 3,
        width: "100%",
        height: "100%",
      }}
    >
      {showLabel && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            mb: 1,
            fontWeight: 400,
            fontSize: "0.875rem",
          }}
        >
          Karte
        </Typography>
      )}

      <Box
        data-map-view
        sx={{
          width: "100%",
          height: height,
          backgroundColor: "transparent",
          borderRadius: showBorder ? "12px" : "16px",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          margin: 0,
          padding: 0,
        }}
        onMouseEnter={() => setIsMapHovered(true)}
        onMouseLeave={() => setIsMapHovered(false)}
      >
        <MapAnimations
          isHovered={isMapHovered}
          layerVisibility={layerVisibility}
          onToggleLayer={toggleLayer}
          showLayerControl={showLayerControl}
          showMenuButton={showMenuButton}
          onShapefileUploadClick={onShapefileUploadClick}
          onAlkisUploadClick={onAlkisUploadClick}
        >
          <MapRenderer
            center={mapCenter}
            zoom={zoom}
            projectName={projectName}
            flaecheCoordinates={flaecheCoordinates}
            anlagenCoordinates={anlagenCoordinates}
            anlagen={anlagen || []}
            layerVisibility={layerVisibility}
            parcels={parcels}
            onParcelUpdate={onParcelUpdate}
            onParcelClick={onParcelClick}
            selectedParcelId={selectedParcelId}
            selectedAnlageId={selectedAnlageId}
            onAnlagePositionChange={onAnlagePositionChange}
            onAnlageSelect={onAnlageSelect}
            onMapClick={onMapClick}
            standortLocation={standortLocation}
          />
        </MapAnimations>
      </Box>
    </Box>
  );
};

export default MapView;
